import { CheckoutSessionStatusResponse } from "@/api/models/api-types";
import { usePaymentCore } from "@/features/payment";
import { stripeService } from "@/features/payment/payment-providers/stripe/services/stripe-service.ts";
import { pushDataLayer } from "@/lib/analytics";
import { useTranslation } from "react-i18next";
import useAuthStore from "@/store/use-auth-store";
import { useToast } from "@/features/toast";

// Satın alma olayının tekrar gönderilmesini engelleyen anahtar. Kullanıcı
// başarı ekranını yenilerse aynı ödeme ikinci kez sayılmamalı.
const PURCHASE_SENT_KEY = "harmony_purchase_sent";

const pushPurchase = (session: CheckoutSessionStatusResponse) => {
  // transaction_id, GA4/Ads/Mixpanel'in üzerinde birleştiği tekilleştirme
  // anahtarı. Yoksa olayı hiç göndermiyoruz — tekilleştirilemeyen bir dönüşüm
  // hiç olmayandan daha kötü.
  //
  // DİKKAT: `payment_intent` yalnızca `payment` modunda (kredi paketi) dolu.
  // Stripe abonelik oturumlarında bu alanı HİÇ doldurmuyor; oraya `invoice`
  // koyuyor. Yalnızca payment_intent'e bakıldığı sürece her abonelik satın
  // alması sessizce düşüyordu — yani asıl ölçmek istediğimiz dönüşüm.
  //
  // Sunucu tarafı da aynı anahtarı üretiyor (fatura olaylarında invoice.id),
  // böylece iki kaynak GA4'te tek satışta birleşiyor.
  const transactionId = session.payment_intent ?? session.invoice;
  if (!transactionId) {
    console.warn("purchase: transaction id yok, olay gönderilmedi", {
      mode: session.mode,
    });
    return;
  }

  try {
    if (sessionStorage.getItem(PURCHASE_SENT_KEY) === transactionId) return;
    sessionStorage.setItem(PURCHASE_SENT_KEY, transactionId);
  } catch {
    // sessionStorage kapalıysa tekilleştirme yapılamaz; olayı yine de
    // gönderiyoruz, tekilleştirme sunucu tarafında transaction_id ile yapılır.
  }

  pushDataLayer({
    event: "purchase",
    transaction_id: transactionId,
    // Stripe'ın gerçekten tahsil ettiği tutar. Fiyat kartındaki ₺ değeri
    // bölgeye göre çevrilmiş bir gösterim; tahsilat USD yapılıyor.
    value: (session.amount_total ?? 0) / 100,
    currency: session.currency?.toUpperCase(),
    user_id: session.metadata?.userId,
    // Tek seferlik alımlar inline `price_data` ile açıldığı için Stripe price
    // id'leri yok; iki akışta da plan id'si kullanılıyor.
    items: [
      {
        item_id: session.metadata?.pricingPlanId,
        item_name: session.metadata?.planName,
      },
    ],
    // Yenilemeler tarayıcıya hiç uğramaz; bu yol yalnızca ilk ödemeyi görür.
    is_renewal: false,
  });
};

export const useStripeCheckout = () => {
  const {
    setSessionData,
    setError,
    setLoading,
    setRedirecting,
    handleSuccess,
    handleError,
    sessionUrl,
  } = usePaymentCore();
  const { refreshUserData } = useAuthStore();
  const { t } = useTranslation();
  const { setUser } = useAuthStore();
  const { success } = useToast();

  const redirectToStripe = async (
    planId: string,
    email: string,
    userId: string
  ) => {
    try {
      setLoading(true);
      setRedirecting(true);

      // Create Stripe checkout session
      const session = await stripeService.createCheckoutSession(
        planId,
        email,
        userId
      );

      if (session?.data.url && session?.data.sessionId) {
        // Store session data
        setSessionData(session.data.sessionId, session.data.url);

        // Redirect to Stripe
        window.location.href = session.data.url;
      } else {
        throw new Error(t("payment.stripe.sessionCreationError"));
      }
    } catch (errorResponse: any) {
      setError(errorResponse.message || t("payment.stripe.redirectError"));
      setLoading(false);
      setRedirecting(false);
    }
  };

  const checkSessionStatus = async (sessionId: string) => {
    if (!sessionId) return;

    try {
      console.log("🔍 Checking session status for:", sessionId);
      setLoading(true);

      const session = await stripeService.getCheckoutSession(sessionId);
      const { status, payment_status } = session;

      console.log("📊 Session status:", { status, payment_status });

      if (status === "complete" && payment_status === "paid") {
        console.log("✅ Payment successful, updating user data...");

        // Satın alma, URL'e değil sunucunun onayladığı oturuma bağlı.
        // Eski Purchase tag'i `payment=success` parametresine bakıyordu ve
        // o parametre hiçbir zaman doğru anda oluşmadığı için hiç çalışmadı.
        pushPurchase(session);
        console.log(
          "🔍 Current refreshUserData function:",
          typeof refreshUserData
        );

        // Auth store'daki refresh metodunu kullan
        try {
          console.log("🚀 Starting refreshUserData call...");
          await refreshUserData();
          console.log("👤 User data refreshed via auth store - SUCCESS!");

          success("Ödeme başarıyla tamamlandı! Kredi bakiyeniz güncellendi.", {
            title: "Ödeme Başarılı",
            duration: 5000,
          });
        } catch (userRefreshError) {
          console.error("❌ Auth store user refresh failed:", userRefreshError);

          // Fallback: Direct API call
          try {
            console.log("🔄 Attempting fallback user data refresh...");
            const userData = await stripeService.refreshUserData();
            console.log("👤 Fallback user data updated:", userData);

            if (userData && userData.data) {
              setUser(userData.data as any);
            }

            success("Ödeme başarıyla tamamlandı!", {
              title: "Ödeme Başarılı",
            });
          } catch (fallbackError) {
            console.error("❌ Fallback user refresh failed:", fallbackError);

            // En kötü durumda bile payment success göster
            success(
              "Ödeme başarıyla tamamlandı! Sayfa yenilendiğinde bakiyeniz güncellenecek.",
              {
                title: "Ödeme Başarılı",
                duration: 7000,
              }
            );
          }
        }

        console.log("🎉 Calling handleSuccess...");
        handleSuccess();
      } else if (status === "expired") {
        console.log("⏰ Session expired");
        handleError("Ödeme oturumu süresi dolmuş. Lütfen tekrar deneyin.");
      } else if (payment_status === "unpaid") {
        console.log("❌ Payment unpaid");
        // Payment failed or pending
        handleError(t("payment.stripe.paymentIncomplete"));
      } else {
        console.log("⏳ Session still open, redirecting to Stripe...");
        // Session hala açık, Stripe'a yönlendir
        if (sessionUrl) {
          window.location.href = sessionUrl;
        }
      }
    } catch (errorResponse: any) {
      console.error("💥 Session durumu kontrol edilemedi:", errorResponse);
      handleError(t("payment.stripe.sessionStatusError"));
    } finally {
      setLoading(false);
    }
  };

  return {
    redirectToStripe,
    checkSessionStatus,
  };
};
